/**
 * Add Plane Wizard Store - Zustand state management for the Add Plane wizard
 *
 * Manages:
 * - Wizard UI state (step, open/close)
 * - Step 0: Aircraft type + tail number (with quick-place shortcut)
 * - Step 1: Client selection/creation (skipped in quick-place mode)
 * - Step 2: Contract type and details (skipped in quick-place mode)
 * - Step 3: Location selection or placement mode
 * - Submission state
 */

import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

// Types for form data
export interface NewClientData {
  client_type: 'individual' | 'corporate' | 'government' | 'non_profit'
  company_name?: string
  first_name?: string
  last_name?: string
  email_primary?: string
  phone_primary?: string
}

export interface NewContactData {
  first_name: string
  last_name: string
  email_primary: string
  phone_mobile?: string
}

export interface AircraftDimensions {
  wingspan: number | null
  length: number | null
  height: number | null
  tail_height: number | null
}

export interface TransientContractData {
  type: 'transient'
  check_in: string // ISO date string
  expected_check_out: string // ISO date string
  booking_type: 'guaranteed' | 'flex'
  rate_type?: 'per_night' | 'flat' | 'per_sqft' | null
  rate_amount?: number | null
}

export interface LeaseContractData {
  type: 'long_term'
  start_date: string // ISO date string
  end_date?: string // ISO date string, optional
  lease_type: 'long_term' | 'month_to_month' | 'seasonal'
  monthly_rate: number // Required for lease_contracts table
  rate_basis?: 'flat' | 'per_sqft' | null
  security_deposit?: number | null
  // Fuel fields
  fuel_type?: 'jet_a' | 'avgas' | null
  fuel_commitment_gallons_monthly?: number | null
  fuel_commitment_gallons_annual?: number | null
  fuel_price_per_gallon?: number | null
  fuel_price_type?: 'fixed' | 'opis_plus' | 'retail_discount' | null
  fuel_price_differential?: number | null
}

export type ContractData = TransientContractData | LeaseContractData

// Presence status options for the wizard (subset of full presence_status enum)
export type WizardPresenceStatus = 'present' | 'away'

// Wizard context — where the wizard was opened from
export type WizardSource = 'ramp' | 'transient' | 'lease' | 'complete_setup'

export interface WizardContext {
  source: WizardSource
  locationId?: string
  returnPath?: string
}

export interface PlacementPosition {
  x: number
  y: number
  z: number
}

// Wizard steps (Aircraft first for quick-drop support)
export type WizardStep = 0 | 1 | 2 | 3
export const WIZARD_STEPS = ['Aircraft', 'Client', 'Contract', 'Location'] as const

// Store state interface
interface AddPlaneWizardState {
  // Wizard UI state
  isOpen: boolean
  currentStep: WizardStep
  isQuickPlaceMode: boolean // Quick path: Aircraft → Location (skips Client & Contract)
  noTailNumber: boolean // User checked "I don't have the tail number"
  wizardContext: WizardContext // Where the wizard was opened from
  existingAllocationId: string | null // For complete_setup: the allocation to update

  // Step 1: Client data
  selectedClientId: string | null
  selectedContactId: string | null
  isCreatingNewClient: boolean
  isCreatingNewContactForExistingClient: boolean // Track if creating new contact for existing client
  newClientData: NewClientData | null
  newContactData: NewContactData | null

  // Step 2: Aircraft data
  selectedExistingAircraftId: string | null // For selecting existing aircraft
  isCreatingNewAircraft: boolean // Toggle between select existing and add new
  selectedAircraftTypeId: string | null
  selectedAircraftTypeName: string | null
  tailNumber: string
  aircraftDimensions: AircraftDimensions | null

  // Suggested fuel type (inferred from aircraft type for auto-fill)
  suggestedFuelType: 'jet_a' | 'avgas' | null

  // Step 3: Contract data
  contractData: ContractData
  isContractTouched: boolean // Track if user has interacted with contract step
  // When the registered aircraft's owner already holds an active lease at this location,
  // we link the new allocation to it instead of creating a transient stay.
  linkedLeaseContractId: string | null

  // Step 4: Location data
  locationMethod: 'dropdown' | 'placement' | null
  selectedAreaId: string | null
  selectedUnitId: string | null
  dropdownPosition: PlacementPosition | null // Position when using dropdown (unit center)
  presenceStatus: WizardPresenceStatus // Whether aircraft is physically present or away

  // Placement mode
  isPlacementModeActive: boolean
  placementPosition: PlacementPosition | null
  placementRotation: number
  detectedAreaId: string | null
  detectedUnitId: string | null

  // Submission state
  isSubmitting: boolean
  submissionError: string | null
}

// Store actions interface
interface AddPlaneWizardActions {
  // Wizard control
  openWizard: (context?: Partial<WizardContext>) => void
  openCompleteSetup: (params: {
    allocationId: string
    aircraftId: string
    tailNumber: string
    typeName: string | null
    defaultFuelType?: 'jet_a' | 'avgas' | null
  }) => void
  closeWizard: () => void
  nextStep: () => void
  prevStep: () => void
  goToStep: (step: WizardStep) => void

  // Quick-place mode
  activateQuickPlace: () => void
  deactivateQuickPlace: () => void
  setNoTailNumber: (noTail: boolean) => void

  // Step 1 actions
  setSelectedClient: (clientId: string | null) => void
  setSelectedContact: (contactId: string | null) => void
  toggleCreateNewClient: (creating: boolean) => void
  toggleCreateNewContactForExistingClient: (creating: boolean) => void
  setNewClientData: (data: NewClientData | null) => void
  setNewContactData: (data: NewContactData | null) => void

  // Step 2 actions
  setSelectedExistingAircraft: (
    aircraftId: string | null,
    tailNumber?: string,
    dimensions?: AircraftDimensions | null,
    typeName?: string | null,
    ownerClientId?: string | null,
    defaultFuelType?: 'jet_a' | 'avgas' | null
  ) => void
  toggleCreateNewAircraft: (creating: boolean) => void
  setAircraftType: (
    typeId: string | null,
    typeName?: string | null,
    dimensions?: AircraftDimensions | null,
    defaultFuelType?: 'jet_a' | 'avgas' | null
  ) => void
  setTailNumber: (tailNumber: string) => void

  // Step 3 actions
  setContractData: (data: ContractData) => void
  /**
   * Seed the contract step from a flight schedule on Register. Defaults to a
   * transient stay with the schedule's arrival/departure dates; if the owner
   * already holds an active lease, records linkedLeaseContractId instead.
   */
  seedContractFromSchedule: (params: {
    checkIn: string | null
    checkOut: string | null
    leaseContractId?: string | null
  }) => void

  // Step 4 actions
  setLocationMethod: (method: 'dropdown' | 'placement') => void
  setSelectedArea: (areaId: string | null) => void
  setSelectedUnit: (unitId: string | null) => void
  setDropdownPosition: (position: PlacementPosition | null) => void
  setPresenceStatus: (status: WizardPresenceStatus) => void

  // Placement mode
  activatePlacementMode: () => void
  deactivatePlacementMode: () => void
  updatePlacementPosition: (position: PlacementPosition, rotation: number) => void
  updateDetectedLocation: (areaId: string | null, unitId: string | null) => void
  confirmPlacement: () => void
  cancelPlacement: () => void

  // Submission
  setSubmitting: (submitting: boolean) => void
  setSubmissionError: (error: string | null) => void

  // Reset
  reset: () => void

  // Validation helpers
  canProceedToStep: (step: WizardStep) => boolean
  isStepComplete: (step: WizardStep) => boolean
  getWizardData: () => WizardSubmissionData
}

// Combined submission data type
export interface WizardSubmissionData {
  // Mode
  isQuickPlaceMode: boolean
  noTailNumber: boolean
  wizardContext: WizardContext
  existingAllocationId: string | null

  // Client
  clientId: string | null
  newClient: NewClientData | null
  newContact: NewContactData | null
  contactId: string | null
  isCreatingNewContactForExistingClient: boolean

  // Aircraft
  existingAircraftId: string | null
  isCreatingNewAircraft: boolean
  aircraftTypeId: string | null
  tailNumber: string
  dimensions: AircraftDimensions | null

  // Contract
  contract: ContractData
  /** When set, link the allocation to this existing lease instead of creating a transient stay. */
  linkedLeaseContractId: string | null

  // Location
  locationMethod: 'dropdown' | 'placement'
  areaId: string | null
  unitId: string | null
  position: PlacementPosition | null
  rotation: number
  presenceStatus: WizardPresenceStatus
}

// Default contract data
const getDefaultContractData = (): TransientContractData => ({
  type: 'transient',
  check_in: new Date().toISOString().split('T')[0],
  expected_check_out: new Date(Date.now() + 86400000).toISOString().split('T')[0], // Tomorrow
  booking_type: 'flex',
  rate_type: 'per_night',
  rate_amount: null,
})

const getDefaultLeaseData = (): LeaseContractData => {
  const today = new Date()
  const oneYearLater = new Date(today)
  oneYearLater.setFullYear(oneYearLater.getFullYear() + 1)
  return {
    type: 'long_term',
    start_date: today.toISOString().split('T')[0],
    end_date: oneYearLater.toISOString().split('T')[0],
    lease_type: 'month_to_month',
    monthly_rate: 0,
    rate_basis: 'flat',
    security_deposit: null,
  }
}

const getDefaultContractForSource = (source: WizardSource): ContractData => {
  if (source === 'lease' || source === 'complete_setup') return getDefaultLeaseData()
  return getDefaultContractData()
}

// Initial state
const initialState: AddPlaneWizardState = {
  isOpen: false,
  currentStep: 0,
  isQuickPlaceMode: false,
  noTailNumber: false,
  wizardContext: { source: 'ramp' },
  existingAllocationId: null,

  // Step 1
  selectedClientId: null,
  selectedContactId: null,
  isCreatingNewClient: false,
  isCreatingNewContactForExistingClient: false,
  newClientData: null,
  newContactData: null,

  // Step 2
  selectedExistingAircraftId: null,
  isCreatingNewAircraft: true, // Default to creating new (current behavior)
  selectedAircraftTypeId: null,
  selectedAircraftTypeName: null,
  tailNumber: '',
  aircraftDimensions: null,

  // Suggested fuel type
  suggestedFuelType: null,

  // Step 3
  contractData: getDefaultContractData(),
  isContractTouched: false,
  linkedLeaseContractId: null,

  // Step 4
  locationMethod: null,
  selectedAreaId: null,
  selectedUnitId: null,
  dropdownPosition: null,
  presenceStatus: 'present' as WizardPresenceStatus,

  // Placement mode
  isPlacementModeActive: false,
  placementPosition: null,
  placementRotation: 0,
  detectedAreaId: null,
  detectedUnitId: null,

  // Submission
  isSubmitting: false,
  submissionError: null,
}

export const useAddPlaneWizardStore = create<
  AddPlaneWizardState & AddPlaneWizardActions
>()(
  devtools(
    (set, get) => ({
      ...initialState,

      // Wizard control
      openWizard: (context) => {
        const ctx: WizardContext = { source: 'ramp', ...context }
        set({
          isOpen: true,
          wizardContext: ctx,
          contractData: getDefaultContractForSource(ctx.source),
          isContractTouched: false,
        })
      },

      openCompleteSetup: (params) => {
        set({
          ...initialState,
          isOpen: true,
          wizardContext: { source: 'complete_setup' },
          existingAllocationId: params.allocationId,
          // Pre-fill aircraft data (read-only in this mode)
          selectedExistingAircraftId: params.aircraftId,
          isCreatingNewAircraft: false,
          tailNumber: params.tailNumber,
          selectedAircraftTypeName: params.typeName,
          suggestedFuelType: params.defaultFuelType ?? null,
          // Start at Client step (step 1)
          currentStep: 1,
          // Default contract for complete_setup
          contractData: getDefaultContractForSource('complete_setup'),
          isContractTouched: false,
        })
      },

      closeWizard: () => set({ isOpen: false }),

      nextStep: () =>
        set((state) => {
          // In quick-place mode, jump from Aircraft (0) to Location (3)
          if (state.isQuickPlaceMode && state.currentStep === 0) {
            return { currentStep: 3 as WizardStep }
          }
          // In complete_setup mode, clamp to steps 1-2
          if (state.wizardContext.source === 'complete_setup') {
            return {
              currentStep: Math.min(state.currentStep + 1, 2) as WizardStep,
            }
          }
          return {
            currentStep: Math.min(state.currentStep + 1, 3) as WizardStep,
          }
        }),

      prevStep: () =>
        set((state) => {
          // In quick-place mode, go from Location (3) back to Aircraft (0)
          if (state.isQuickPlaceMode && state.currentStep === 3) {
            return { currentStep: 0 as WizardStep, isQuickPlaceMode: false }
          }
          // In complete_setup mode, clamp to steps 1-2
          if (state.wizardContext.source === 'complete_setup') {
            return {
              currentStep: Math.max(state.currentStep - 1, 1) as WizardStep,
            }
          }
          return {
            currentStep: Math.max(state.currentStep - 1, 0) as WizardStep,
          }
        }),

      goToStep: (step) => set({ currentStep: step }),

      // Quick-place mode
      activateQuickPlace: () =>
        set({
          isQuickPlaceMode: true,
          currentStep: 3, // Jump to location step
        }),

      deactivateQuickPlace: () =>
        set({
          isQuickPlaceMode: false,
          currentStep: 0, // Back to aircraft step
        }),

      setNoTailNumber: (noTail) =>
        set({
          noTailNumber: noTail,
          // Clear tail number when toggling on
          tailNumber: noTail ? '' : get().tailNumber,
        }),

      // Step 1 actions
      setSelectedClient: (clientId) =>
        set({
          selectedClientId: clientId,
          // Clear new client data when selecting existing
          isCreatingNewClient: false,
          isCreatingNewContactForExistingClient: false,
          newClientData: null,
          newContactData: null,
          // Clear contact when client changes
          selectedContactId: null,
        }),

      setSelectedContact: (contactId) => set({ selectedContactId: contactId }),

      toggleCreateNewClient: (creating) =>
        set({
          isCreatingNewClient: creating,
          // Clear existing selection when creating new
          selectedClientId: creating ? null : get().selectedClientId,
          selectedContactId: null,
          isCreatingNewContactForExistingClient: false,
        }),

      toggleCreateNewContactForExistingClient: (creating) =>
        set({
          isCreatingNewContactForExistingClient: creating,
          // Clear contact selection when toggling to create new
          selectedContactId: creating ? null : get().selectedContactId,
          // Clear new contact data when toggling back to select existing
          newContactData: creating ? get().newContactData : null,
        }),

      setNewClientData: (data) => set({ newClientData: data }),

      setNewContactData: (data) => set({ newContactData: data }),

      // Step 2 actions
      setSelectedExistingAircraft: (aircraftId, tailNumber, dimensions, typeName, ownerClientId, defaultFuelType) =>
        set({
          selectedExistingAircraftId: aircraftId,
          tailNumber: tailNumber || '',
          aircraftDimensions: dimensions ?? null,
          selectedAircraftTypeName: typeName ?? null,
          isCreatingNewAircraft: false,
          suggestedFuelType: defaultFuelType ?? null,
          // Auto-fill client from aircraft owner
          ...(ownerClientId ? {
            selectedClientId: ownerClientId,
            isCreatingNewClient: false,
            newClientData: null,
            newContactData: null,
            selectedContactId: null,
            isCreatingNewContactForExistingClient: false,
          } : {}),
        }),

      toggleCreateNewAircraft: (creating) =>
        set({
          isCreatingNewAircraft: creating,
          // Clear existing selection when toggling to create new
          selectedExistingAircraftId: creating ? null : get().selectedExistingAircraftId,
          // Clear new aircraft fields when toggling to select existing
          selectedAircraftTypeId: creating ? get().selectedAircraftTypeId : null,
          tailNumber: creating ? get().tailNumber : '',
          aircraftDimensions: creating ? get().aircraftDimensions : null,
        }),

      setAircraftType: (typeId, typeName, dimensions, defaultFuelType) =>
        set({
          selectedAircraftTypeId: typeId,
          selectedAircraftTypeName: typeName ?? null,
          aircraftDimensions: dimensions ?? null,
          suggestedFuelType: defaultFuelType ?? null,
        }),

      setTailNumber: (tailNumber) =>
        set({ tailNumber: tailNumber.toUpperCase() }),

      // Step 3 actions
      setContractData: (data) => set({ contractData: data, isContractTouched: true }),

      seedContractFromSchedule: ({ checkIn, checkOut, leaseContractId }) => {
        // Owner already leases here → link the existing lease, leave contract type as-is.
        if (leaseContractId) {
          set({ linkedLeaseContractId: leaseContractId, isContractTouched: true })
          return
        }
        const fallback = getDefaultContractData()
        set({
          linkedLeaseContractId: null,
          isContractTouched: true,
          contractData: {
            type: 'transient',
            check_in: checkIn ? checkIn.split('T')[0] : fallback.check_in,
            expected_check_out: checkOut ? checkOut.split('T')[0] : fallback.expected_check_out,
            booking_type: 'flex',
            rate_type: 'per_night',
            rate_amount: null,
          },
        })
      },

      // Step 4 actions
      setLocationMethod: (method) => set({ locationMethod: method }),

      setSelectedArea: (areaId) =>
        set({
          selectedAreaId: areaId,
          // Clear unit when area changes
          selectedUnitId: null,
        }),

      setSelectedUnit: (unitId) => set({ selectedUnitId: unitId }),

      setDropdownPosition: (position) => set({ dropdownPosition: position }),

      setPresenceStatus: (status) => set({ presenceStatus: status }),

      // Placement mode
      activatePlacementMode: () =>
        set({
          isPlacementModeActive: true,
          isOpen: false, // Close modal when entering placement mode
          locationMethod: 'placement',
        }),

      deactivatePlacementMode: () =>
        set({
          isPlacementModeActive: false,
        }),

      updatePlacementPosition: (position, rotation) =>
        set({
          placementPosition: position,
          placementRotation: rotation,
        }),

      updateDetectedLocation: (areaId, unitId) =>
        set({
          detectedAreaId: areaId,
          detectedUnitId: unitId,
        }),

      confirmPlacement: () =>
        set((state) => ({
          isPlacementModeActive: false,
          // Use detected location as selected
          selectedAreaId: state.detectedAreaId,
          selectedUnitId: state.detectedUnitId,
        })),

      cancelPlacement: () =>
        set({
          isPlacementModeActive: false,
          isOpen: true, // Reopen wizard
          locationMethod: null,
          placementPosition: null,
          placementRotation: 0,
          detectedAreaId: null,
          detectedUnitId: null,
        }),

      // Submission
      setSubmitting: (submitting) => set({ isSubmitting: submitting }),

      setSubmissionError: (error) => set({ submissionError: error }),

      // Reset
      reset: () => set(initialState),

      // Validation helpers
      isStepComplete: (step) => {
        const state = get()
        const isCompleteSetup = state.wizardContext.source === 'complete_setup'

        switch (step) {
          case 0: // Aircraft (first step) — always complete in complete_setup mode
            if (isCompleteSetup) return true
            if (state.isCreatingNewAircraft) {
              // Need aircraft type; tail number optional if noTailNumber checked
              if (!state.selectedAircraftTypeId) return false
              if (!state.noTailNumber && !state.tailNumber.trim()) return false
              return true
            }
            // Using existing aircraft
            return !!state.selectedExistingAircraftId

          case 1: // Client (skipped in quick-place mode)
            if (state.isQuickPlaceMode) return true

            if (state.isCreatingNewClient) {
              const client = state.newClientData
              const contact = state.newContactData
              if (!client || !contact) return false

              const hasName =
                client.client_type === 'individual'
                  ? !!client.first_name && !!client.last_name
                  : !!client.company_name

              const hasContact =
                !!contact.first_name &&
                !!contact.last_name &&
                !!contact.email_primary

              return hasName && hasContact
            }
            // Existing client selected
            if (!state.selectedClientId) return false

            // If creating new contact for existing client, validate contact data
            if (state.isCreatingNewContactForExistingClient) {
              const contact = state.newContactData
              if (!contact) return false
              return (
                !!contact.first_name &&
                !!contact.last_name &&
                !!contact.email_primary
              )
            }

            // Existing client with existing/no contact is valid
            return true

          case 2: // Contract (skipped in quick-place mode)
            if (state.isQuickPlaceMode) return true

            // Must be touched by user AND have valid data
            if (!state.isContractTouched) return false
            if (state.contractData.type === 'transient') {
              return (
                !!state.contractData.check_in &&
                !!state.contractData.expected_check_out
              )
            }
            return !!state.contractData.start_date

          case 3: // Location — always complete in complete_setup mode (no location step)
            if (isCompleteSetup) return true
            // Either dropdown selection or placement
            if (state.locationMethod === 'dropdown') {
              // For lease source, require unit selection
              if (state.wizardContext.source === 'lease') {
                return !!state.selectedUnitId
              }
              return true
            }
            if (state.locationMethod === 'placement') {
              return !!state.placementPosition
            }
            return false

          default:
            return false
        }
      },

      canProceedToStep: (step) => {
        const state = get()
        const isCompleteSetup = state.wizardContext.source === 'complete_setup'
        for (let i = 0; i < step; i++) {
          // In quick-place mode, skip validation of steps 1-2
          if (state.isQuickPlaceMode && (i === 1 || i === 2)) continue
          // In complete_setup mode, skip validation of steps 0 and 3
          if (isCompleteSetup && (i === 0 || i === 3)) continue
          if (!state.isStepComplete(i as WizardStep)) {
            return false
          }
        }
        return true
      },

      getWizardData: () => {
        const state = get()
        // Use dropdownPosition when using dropdown method, placementPosition when using placement mode
        const position =
          state.locationMethod === 'dropdown'
            ? state.dropdownPosition
            : state.placementPosition
        // Include newContact only when creating new client or new contact for existing client
        const shouldIncludeNewContact =
          state.isCreatingNewClient || state.isCreatingNewContactForExistingClient
        return {
          isQuickPlaceMode: state.isQuickPlaceMode,
          noTailNumber: state.noTailNumber,
          wizardContext: state.wizardContext,
          existingAllocationId: state.existingAllocationId,
          clientId: state.selectedClientId,
          newClient: state.newClientData,
          newContact: shouldIncludeNewContact ? state.newContactData : null,
          contactId: state.selectedContactId,
          isCreatingNewContactForExistingClient:
            state.isCreatingNewContactForExistingClient,
          existingAircraftId: state.selectedExistingAircraftId,
          isCreatingNewAircraft: state.isCreatingNewAircraft,
          aircraftTypeId: state.selectedAircraftTypeId,
          tailNumber: state.tailNumber,
          dimensions: state.aircraftDimensions,
          contract: state.contractData,
          linkedLeaseContractId: state.linkedLeaseContractId,
          locationMethod: state.locationMethod || 'dropdown',
          areaId: state.selectedAreaId || state.detectedAreaId,
          unitId: state.selectedUnitId || state.detectedUnitId,
          position: position,
          rotation: state.placementRotation,
          presenceStatus: state.presenceStatus,
        }
      },
    }),
    { name: 'add-plane-wizard-store' }
  )
)

// Selector helpers
export const selectIsWizardOpen = (state: AddPlaneWizardState) => state.isOpen
export const selectCurrentStep = (state: AddPlaneWizardState) =>
  state.currentStep
export const selectIsPlacementMode = (state: AddPlaneWizardState) =>
  state.isPlacementModeActive
export const selectIsQuickPlaceMode = (state: AddPlaneWizardState) =>
  state.isQuickPlaceMode

// Helper to generate a temp tail number
export function generateTempTailNumber(): string {
  const now = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `TEMP_${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`
}

// Helper to check if a tail number is temporary
export function isTempTailNumber(tailNumber: string): boolean {
  return tailNumber.startsWith('TEMP_')
}
